/**
 * 15 #9 — replies the reply-inbox job read from Sara's shared mailbox (needs Incoming email + AI extras,
 * both off until IT sets them up and an admin switches them on). Each one waits for a person: Check → the reply form opens filled with what the
 * AI read (code, date, comments) for the drawings it matched → Save reply records it through the normal routes;
 * Dismiss drops it. Nothing is recorded on a drawing without that confirmation.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { notify } from '@/components/ui/notify';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { sdRequest } from './api';
import { REPLY_CODES } from './model';
import { ReplyCodeBadge, ReplyForm } from './ReplyForm';
import { Callout } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });

export function useReplyInbox(active) {
  const [state, setState] = useState({ items: [], features: null });
  const load = useCallback(async () => {
    try {
      const res = await sdRequest('get', '/reply-inbox');
      setState({ items: res.data?.items || [], features: res.data?.features || null });
    } catch {
      setState({ items: [], features: null });
    }
  }, []);
  useEffect(() => { if (active) load(); }, [active, load]);
  return { ...state, reload: load };
}

function Suggestion({ item, rowsByKey, onRecordReply, onClosed }) {
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const rows = (item.items || []).map((match) => rowsByKey.get(`${match.id}|${match.index}`)).filter((row) => row && row.replyState !== 'replied');
  const code = REPLY_CODES.find((entry) => entry.code === item.code);
  const close = async (action) => {
    setBusy(true);
    try {
      await sdRequest('post', `/reply-inbox/${encodeURIComponent(item.id)}/${action}`, {});
      onClosed(item.id);
    } catch (err) {
      notify.error('Could not update the reply', { description: getErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-xl border border-line px-3 py-2.5" data-testid={`reply-suggestion-${item.id}`}>
      <div className="flex items-start gap-2.5">
        {code ? <ReplyCodeBadge code={code.code} tone={code.tone} /> : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-bold text-ink" title={item.subject}>{item.subject || '(no subject)'}</p>
          <p className="truncate text-[11.5px] text-ink-3">from {item.from || 'unknown'} · {item.received_at ? formatDateTime(item.received_at) : 'no date'} · AI {item.confidence || 'low'} confidence</p>
          <p className="mt-1 text-[12.5px] text-ink-2">
            {code ? <b className="font-semibold text-ink">{code.label}</b> : 'No code'}{item.reply_date ? ` · replied ${formatDate(item.reply_date)}` : ''} · {fmt((item.items || []).length)} {(item.items || []).length === 1 ? 'drawing' : 'drawings'}: {(item.items || []).slice(0, 4).map((match) => match.drawing_id || match.title).join(', ')}
          </p>
          {item.comments ? <p className="mt-1 line-clamp-2 rounded-md bg-surface-2 px-2 py-1 text-[12px] text-ink-2">{item.comments}</p> : null}
        </div>
      </div>
      {checking && rows.length ? (
        <div className="mt-2.5 rounded-lg border border-line-2 p-2.5">
          <ReplyForm
            rows={rows}
            fromEmail={false}
            initial={{ reply: code?.value || '', date: item.reply_date || undefined, comments: item.comments || '' }}
            onSave={async (form) => { await onRecordReply(rows, form); await close('applied'); }}
            onCancel={() => setChecking(false)}
            testId={`reply-suggestion-form-${item.id}`}
          />
        </div>
      ) : null}
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        {!rows.length ? <span className="mr-auto text-[12px] text-ink-3">These submissions already have a reply.</span> : null}
        <Button type="button" variant="ghost" size="xs" onClick={() => close('dismiss')} disabled={busy} data-testid={`reply-suggestion-dismiss-${item.id}`}>
          {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null} Dismiss
        </Button>
        {rows.length && !checking ? (
          <Button type="button" size="xs" onClick={() => setChecking(true)} data-testid={`reply-suggestion-check-${item.id}`}>Check and save</Button>
        ) : null}
      </div>
    </li>
  );
}

export function ReplyInbox({ inbox, rows, onRecordReply }) {
  const [open, setOpen] = useState(false);
  const rowsByKey = useMemo(() => new Map(rows.map((row) => [`${row.drawing.id}|${row.index}`, row])), [rows]);
  const [closed, setClosed] = useState(() => new Set());
  const items = inbox.items.filter((item) => !closed.has(item.id));
  if (!items.length) return null;
  return (
    <>
      <Callout
        tone="navy"
        icon={Inbox}
        testId="reply-inbox-callout"
        title={<b className="font-bold">{fmt(items.length)} consultant {items.length === 1 ? 'reply was' : 'replies were'} read from the mailbox</b>}
        sub="The AI matched them to drawings awaiting a reply. Check each one before it is saved."
        actions={<Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="reply-inbox-open">Check {fmt(items.length)}</Button>}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-2xl border-line bg-surface text-ink" data-testid="reply-inbox-dialog">
          <DialogHeader className="text-left">
            <DialogTitle className="font-heading text-[17px] font-bold text-ink">Replies read from the mailbox</DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">Nothing is recorded until you save it.</DialogDescription>
          </DialogHeader>
          <ul className="space-y-2">
            {items.map((item) => (
              <Suggestion
                key={item.id}
                item={item}
                rowsByKey={rowsByKey}
                onRecordReply={onRecordReply}
                onClosed={(id) => { setClosed((current) => new Set(current).add(id)); inbox.reload(); }}
              />
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  );
}
